import {
  CITATION_DELIMITER,
  CITATION_START,
  CITATION_STOP,
  parseCitations,
  serializeCitations,
  stripCitationTokens,
} from "../../../features/narrative-citations/parseCitations";
import type { CitationGroup } from "../../../features/narrative-citations/types";

export function getNarrativeEditorValue(
  value: unknown,
): { rawValue: string; displayedValue: string } {
  const rawValue = typeof value === "string" ? value : "";
  return {
    rawValue,
    displayedValue: stripCitationTokens(rawValue),
  };
}

/**
 * Remove citation sentinels from TEXTAREA INPUT (typed or pasted). Complete
 * clusters are deleted whole; leftover lone sentinels are deleted char-wise.
 * Deliberately NOT stripCitationTokens: its truncated-cluster rule consumes
 * everything after a lone U+E200, which for stored narratives is a safety
 * property but for live input would eat the officer's prose after a stray
 * pasted sentinel.
 */
function sanitizeEditorInput(text: string): string {
  if (
    !text.includes(CITATION_START) &&
    !text.includes(CITATION_DELIMITER) &&
    !text.includes(CITATION_STOP)
  ) {
    return text;
  }
  return text
    .replace(
      new RegExp(
        `${CITATION_START}[^${CITATION_START}${CITATION_STOP}]*${CITATION_STOP}`,
        "gu",
      ),
      "",
    )
    .replace(
      new RegExp(`[${CITATION_START}${CITATION_DELIMITER}${CITATION_STOP}]`, "gu"),
      "",
    );
}

/**
 * The single contiguous change between two versions of a text: common prefix
 * and suffix trimmed, [start, oldEnd) replaced by [start, newEnd). A single
 * textarea change event is always one contiguous replacement; anything more
 * exotic (multi-site name correction) collapses to the region spanning all
 * differences, which only ever OVER-drops — never under-drops — citations.
 */
function contiguousChange(
  oldText: string,
  newText: string,
): { start: number; oldEnd: number; newEnd: number } {
  const maxPrefix = Math.min(oldText.length, newText.length);
  let start = 0;
  while (start < maxPrefix && oldText[start] === newText[start]) start += 1;
  const maxSuffix = maxPrefix - start;
  let suffix = 0;
  while (
    suffix < maxSuffix &&
    oldText[oldText.length - 1 - suffix] === newText[newText.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  return {
    start,
    oldEnd: oldText.length - suffix,
    newEnd: newText.length - suffix,
  };
}

/**
 * Serialize surviving groups, then PROVE each survivor still renders exactly
 * its pre-edit anchor prose. Anchors are re-derived from sentence boundaries
 * on every parse, so an edit that changes segmentation (a period turned into
 * a comma, a dropped neighboring citation changing the anchor floor, a word
 * inserted at the anchor's start) can silently regrow a kept citation's
 * highlight over prose the officer just wrote — presenting unreviewed text
 * as evidence-grounded. Any survivor whose re-derived anchor differs is
 * dropped and the remainder re-verified (dropping a group can itself move a
 * later group's anchor floor), until the persisted form is self-consistent.
 */
function serializeVerified(
  nextClean: string,
  survivors: Array<{ group: CitationGroup; expectedAnchorText: string }>,
): string {
  let kept = survivors;
  while (kept.length > 0) {
    const candidate = serializeCitations(
      nextClean,
      kept.map((entry) => entry.group),
    );
    const reparsed = parseCitations(candidate);
    if (reparsed.groups.length !== kept.length) {
      // Groups merged or vanished on re-parse — the mapping between what we
      // decided to keep and what would render is no longer sound. Fail safe.
      return nextClean;
    }
    const dropped = new Set<number>();
    reparsed.groups.forEach((group, index) => {
      const anchorText = reparsed.cleanText.slice(
        group.anchorStart,
        group.anchorEnd,
      );
      if (anchorText !== kept[index].expectedAnchorText) dropped.add(index);
    });
    if (dropped.size === 0) return candidate;
    kept = kept.filter((_, index) => !dropped.has(index));
  }
  return nextClean;
}

/**
 * Fold an officer's textarea edit back into the token-bearing raw value,
 * keeping every citation whose anchored prose the edit provably did not
 * change.
 *
 * A citation asserts THIS sentence is grounded in THAT evidence, so a group
 * whose anchor overlaps the edited region is dropped; groups before it keep
 * their offsets and groups after it shift by the length delta — and every
 * survivor must then re-render byte-identical anchor prose
 * (serializeVerified). Each citation's grounding fields reference the
 * EVIDENCE (not the narrative prose), so surviving clusters re-verify
 * server-side unchanged.
 *
 * The controlled-input no-op (the textarea echoing the already-clean display
 * value) still preserves the original raw value, and clearing the field
 * still propagates the caller's undefined/empty shape. Sentinels pasted into
 * the buffer are stripped before diffing — re-serializing around foreign
 * sentinels would corrupt (or forge) clusters.
 */
export function reconcileNarrativeEditorChange(
  rawValue: string,
  displayedValue: string,
  nextValue: string | undefined,
): string | undefined {
  if (rawValue !== displayedValue && (nextValue ?? "") === displayedValue) {
    return rawValue;
  }
  if ((nextValue ?? "") === "") {
    // Clearing a narrative drops everything, in the caller's shape.
    return nextValue;
  }

  const next = sanitizeEditorInput(nextValue ?? "");
  if (rawValue === displayedValue) {
    // Nothing to preserve, but pasted metadata must still be removed.
    return next;
  }
  const { cleanText, groups } = parseCitations(rawValue);
  // The editor buffer is derived via stripCitationTokens; if it ever
  // disagrees with parseCitations' clean text, offsets can't be trusted —
  // fall back to the historical drop-everything behavior.
  if (groups.length === 0 || cleanText !== displayedValue) {
    return next;
  }

  const change = contiguousChange(cleanText, next);
  const delta = next.length - cleanText.length;
  const survivors = groups.flatMap((group) => {
    // A zero-width anchor cites no visible prose; there is nothing to prove
    // unchanged, so any genuine edit drops it rather than letting it ride
    // through rewrites indefinitely.
    if (group.anchorStart >= group.anchorEnd) return [];
    const expectedAnchorText = cleanText.slice(
      group.anchorStart,
      group.anchorEnd,
    );
    if (group.anchorEnd <= change.start) {
      return [{ group, expectedAnchorText }];
    }
    if (group.anchorStart >= change.oldEnd) {
      return [
        {
          group: {
            ...group,
            anchorStart: group.anchorStart + delta,
            anchorEnd: group.anchorEnd + delta,
          },
          expectedAnchorText,
        },
      ];
    }
    // The edit touched the anchored span: its provenance claim no longer
    // holds for the rewritten sentence.
    return [];
  });

  return serializeVerified(next, survivors);
}
